import React, { useCallback, useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import { read, utils } from 'xlsx';
import { CREATE_USERS } from 'common/constants/user';
import { MessageBox } from 'components/';
import { TitleArray } from 'components/CircleIconWithText/types';
import { areAllObjectsValid } from 'utilities';
import { Logger } from 'utilities/logger';
import { ActionDialogPickerProps } from './ActionDialogPicker.props';
import { actionDialogPickerClasses, ActionDialogPickerRoot } from './ActionDialogPicker.styles';
import { UserProfileInfo } from '../ActionButtonGroup/ActionButtonGroup.props';
import getDialogComponent from './Components/DialogComponent';

const ActionDialogPicker = ({
  dialogUserData,
  handleDialogClose,
  firm,
  handleAddUserToFirm,
  selectedCompany,
  companyList,
  fundList,
  company,
  firmUsers,
}: ActionDialogPickerProps) => {
  const [newUsers, setNewUsers] = useState<UserProfileInfo[] | undefined>(undefined);

  const onLoad = (event: ProgressEvent<FileReader>) => {
    if (!event.target) {
      return;
    }
    try {
      const bufferArray = event.target.result;
      const wb = read(bufferArray, { type: 'buffer' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      if (areAllObjectsValid({ objects: utils.sheet_to_json(ws), keys: ['email', 'first_name', 'last_name'] })) {
        setNewUsers(utils.sheet_to_json(ws));
      } else {
        setNewUsers([]);
      }
    } catch (error) {
      Logger.error('Error reading file', error);
      setNewUsers([]);
    }
  };

  const readFile = ({ file }: { file?: File }) => {
    if (!file) {
      return;
    }
    const fileReader = new FileReader();
    fileReader.onload = onLoad;
    fileReader.readAsArrayBuffer(file);
  };

  const displayMessageBox = useCallback(() => {
    if (newUsers && newUsers.length > 0) {
      return (
        <MessageBox
          title=""
          action={
            <ActionDialogPickerRoot className={actionDialogPickerClasses.textSuccess}>
              <span>{`You are uploading ${newUsers?.length} users. Click `}</span>
              <span
                style={{
                  fontWeight: 'bold',
                }}>
                {CREATE_USERS}
              </span>
              <span> to confirm.</span>
            </ActionDialogPickerRoot>
          }
          type="success"
          fullWidth={false}
          hasFrame={false}
        />
      );
    }
    return (
      <MessageBox
        title=""
        action={
          <div className={actionDialogPickerClasses.textError}>
            <span>The file you are trying to upload is invalid. Click </span>
            {/* Reset newUsers to display the drop zone again */}
            <Typography
              variant="inherit"
              className={actionDialogPickerClasses.mimicLink}
              onClick={() => setNewUsers(undefined)}>
              here
            </Typography>
            <span> to try again.</span>
          </div>
        }
        type="error"
        fullWidth={false}
        hasFrame={false}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newUsers]);

  const resetDropzone = useCallback(() => {
    handleDialogClose();
    setNewUsers(undefined);
  }, [handleDialogClose]);

  const titleArray: TitleArray = [
    ['Drag & Drop', null],
    ['or', null],
    ['Select a file', { textDecoration: 'underline' }],
  ];

  const dialog = useMemo(
    () =>
      getDialogComponent({
        type: dialogUserData.type,
        props: {
          bulkAddProps: {
            firm,
            resetDropzone,
            dialogUserData,
            newUsers,
            readFile,
            titleArray,
            displayMessageBox,
            selectedCompany,
            companyList,
            fundList,
          },
          inviteUserProps: {
            handleDialogClose,
            dialogUserData,
            handleAddUserToFirm,
            company,
          },
          manageCompanyUsersProps: {
            handleDialogClose,
            dialogUserData,
            companyList,
            firmUsers,
            firm,
            fundList,
          },
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dialogUserData,
      firm,
      resetDropzone,
      newUsers,
      handleDialogClose,
      handleAddUserToFirm,
      company,
      companyList,
      fundList,
      selectedCompany,
      displayMessageBox,
      firmUsers,
    ]
  );

  return dialogUserData.open && dialog ? (dialog as React.ReactElement) : null;
};

export default ActionDialogPicker;
